import { useEffect, useState } from "react";
import Pagination from "../Pagination";
import { IoAdd, IoSearchOutline } from "react-icons/io5";
import { FaPhone, FaRegCopy, FaWhatsapp } from "react-icons/fa6";
import {
  fetchCategories,
  formatNumber,
  getAllStateUsers,
  getStateGovernmentUsers,
} from "../../redux/thunk";
import StateGovModal from "../modals/StateGovModal";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import LoadingSpinner from "../elements/LoadingSpinner";

const StateUsers = ({
  isLoadingDelete = false,
  handleDeleteClick = () => {},
  handleCopyClick = () => {},
  handleBvnClick,
  handleViewAllBank,
  handleAddBank,
  handleUpdateCreditLimit,
  page,
  setUnmountModal,
  unmountModal,
}: any) => {
  const [searchedUser, setSearchedUser] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stateUsers, setStateUsers] = useState([]);
  const [itemsPerPage] = useState(20);
  const [filter, setFilter] = useState("");
  const [allMinistries, setAllMinistries] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showEmploymentDetails, setShowEmploymentDetails] = useState(false);
  const [employmentDetails, setEmploymentDetails] = useState({});
  const [viewAddress, setViewAddress] = useState({ show: false, address: "" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);

  const handleEmploymentDetails = (data: any) => {
    setEmploymentDetails(data);
    setShowEmploymentDetails(true);
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    setIsLoading(true);
    const handler = setTimeout(() => {
      Promise.resolve(
        dispatch(
          getStateGovernmentUsers({
            page: currentPage,
            limit: itemsPerPage,
            search: searchedUser
          })
        )
      ).then((response: any) => {
        setStateUsers(response?.payload?.users || []);
        setCurrentPage(response?.payload?.currentPage || 1);
        setTotalPages(response?.payload?.totalPages || 1);
        setTotalResults(response?.payload?.totalResults || 0);
        setUnmountModal(false);
        setIsLoading(false);
      });
    }, 500); // 500ms debounce

    return () => clearTimeout(handler);
  }, [dispatch, currentPage, itemsPerPage, unmountModal, searchedUser]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchedUser, filter]);

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Pagination
  const goToPage = async (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const searchForUsersWithEmail = (value: string) => {
    setSearchedUser(value);
  };

  useEffect(() => {
    const fetchAndSetCategories = async () => {
      const allCategories = await fetchCategories();
      setAllMinistries(allCategories);
    };
    fetchAndSetCategories();
  }, []);

  useEffect(() => {
    if (filter) {
      const filteredByName = stateUsers.filter(
        (user: any) => user.stateGovernmentData?.ministryId === filter
      );
      setFilteredUsers(filteredByName);
    } else {
      setFilteredUsers(stateUsers);
    }
  }, [filter, stateUsers]);

  const mainData = filteredUsers;
  const adminLevel = sessionStorage.getItem("adminLevel");

  return (
    <div className="bg-white rounded-md shadow-md pb-6">
      <h1 className="text-base font-semibold ">State Users</h1>

      <div className="w-full overflow-x-auto">
        <div
          className="flex items-center justify-between p-6"
          style={{ minWidth: "700px" }}
        >
          <div className="flex justify-between w-full">
            <div className="relative md:w-[30rem] w-fit">
              <IoSearchOutline className="w-6 h-6 absolute top-[0.6rem] left-2 text-gray-300" />
              <input
                type="search"
                name="searchedUser"
                id="searchedUser"
                value={searchedUser}
                onChange={(e) => searchForUsersWithEmail(e.target.value)}
                placeholder="Search user using email"
                className="border p-2 text-sm rounded-md indent-7 w-full"
              />
            </div>
            <div>
              <select
                className="border p-2 rounded-md text-sm"
                onChange={(e) => setFilter(e.target.value)}
                value={filter}
                name="filter"
              >
                <option value="">All ministries</option>
                {allMinistries?.map((ministry: any, index: number) => (
                  <option key={index} value={ministry._id}>
                    {ministry.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <section className="overflow-x-auto">
          <table className="w-[1020px]" style={{ minWidth: "700px" }}>
            <thead className="bg-gray-50 font-bold p-4 text-left">
              <tr>
                <th className="p-2 ">S/N</th>
                <th className="p-2 text-nowrap">Name</th>
                <th className="p-2 text-nowrap">Email</th>
                <th className="p-2 text-nowrap">Phone Number</th>
                <th className="p-2 text-nowrap">Contact</th>
                <th className="p-2 text-nowrap">Office Address</th>
                <th className="p-2 text-nowrap">Ministry</th>
                <th className="p-2">BVN</th>
                <th className="p-2 text-nowrap">Add Bank</th>
                <th className="p-2 text-nowrap">All Banks</th>
                <th className="p-2 text-nowrap">Employment</th>
                <th className="p-2 text-nowrap">Credit Score(Balance)</th>
                <th className="p-2 text-nowrap">Credit Score</th>
                {page !== "userDetails" && (
                  <th className="p-2 text-nowrap">Open Password</th>
                )}
                <th className="p-2 text-nowrap">Action</th>
                {adminLevel === "superadmin" && (
                  <th className="p-2 text-nowrap">Force Delete User</th>
                )}
              </tr>
            </thead>
            <tbody className="px-4">
              {isLoading ? (
                <tr className="p-2">
                  <td colSpan={10} className="text-sm text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : filter?.length > 0 && filteredUsers?.length === 0 ? (
                <tr className="p-2">
                  <td colSpan={10} className="text-sm text-center">
                    No user in this ministry
                  </td>
                </tr>
              ) : mainData?.length > 0 ? (
                mainData?.map((data: any, index: any) => (
                  <tr className="border-b border-gray-300 py-2" key={index}>
                    <td className="text-secondary p-2">
                      {index + ((currentPage - 1) * itemsPerPage + 1)}
                    </td>
                    <td className="p-2 text-nowrap">
                      {data.lastName} {data.firstName}
                    </td>
                    <td className="p-2">
                      <div className="flex justify-between gap-2 items-center">
                        <p>{data.email}</p>
                        <button
                          type="button"
                          className="mr-3"
                          onClick={() => handleCopyClick(data.email)}
                        >
                          <FaRegCopy className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                    </td>
                    <td className="p-2 text-nowrap">{data.phoneNumber}</td>
                    <td className="p-2">
                      <button
                        className="mr-3"
                        type="button"
                        title="call customer"
                      >
                        <a href={`tel:${data?.phoneNumber}`}>
                          <FaPhone />
                        </a>
                      </button>
                      <button type="button" title="text via whatsapp">
                        <a
                          href={`https://wa.me/${data?.phoneNumber?.replace(
                            /^0/,
                            "234"
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaWhatsapp />
                        </a>
                      </button>
                    </td>
                    <td className="p-2 text-nowrap">
                      <button
                        type="button"
                        className="text-secondary text-sm border border-secondary p-2"
                        onClick={() =>
                          setViewAddress({
                            show: true,
                            address: data?.officeAddress,
                          })
                        }
                      >
                        View
                      </button>
                    </td>
                    <td className="p-2">
                      {
                        data.stateGovernmentData.stateGovernmentId.categoryType.filter(
                          (ministry: any) =>
                            data.stateGovernmentData.ministryId === ministry._id
                        )[0].name
                      }
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-2 justify-between">
                        <button
                          onClick={() => handleBvnClick(data)}
                          className="text-blue-500 hover:underline focus:outline-none"
                        >
                          {data.bvn}
                        </button>
                        <button
                          type="button"
                          className=""
                          onClick={() => handleCopyClick(data.bvn)}
                        >
                          <FaRegCopy className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <button
                        className="text-secondary border"
                        onClick={() =>
                          handleAddBank(data.firstName, data.lastName, data._id)
                        }
                      >
                        <IoAdd className="w-9 h-9" />
                      </button>
                    </td>
                    <td className="p-2 text-center">
                      <button
                        className="bg-secondary text-white text-sm rounded-md p-2"
                        onClick={() =>
                          handleViewAllBank(data._id, data.linkedBanks)
                        }
                      >
                        View
                      </button>
                    </td>
                    <td className="p-2 text-nowrap">
                      <button
                        onClick={() =>
                          handleEmploymentDetails(data.stateGovernmentData)
                        }
                        className="text-secondary text-sm border border-secondary p-2"
                      >
                        View
                      </button>
                    </td>

                    <td className="p-2">
                      {data.creditScore !== undefined &&
                      data.creditScore !== null
                        ? `₦${formatNumber(data?.creditScore)}`
                        : "--"}
                    </td>
                    <td className="p-2">
                      {data.creditLimit !== undefined &&
                      data.creditLimit !== null
                        ? `₦${formatNumber(data?.creditLimit)}`
                        : "--"}
                    </td>
                    {page !== "userDetails" && (
                      <td className="p-2">
                        {data.userPassword ? data.userPassword : "not found"}
                      </td>
                    )}
                    <td className="p-2 text-nowrap">
                      <button
                        type="button"
                        className="p-2 text-sm text-secondary border border-secondary"
                        onClick={() => handleUpdateCreditLimit(data)}
                      >
                        Update Credit Limit
                      </button>
                    </td>
                    {adminLevel === "superadmin" && (
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          className={`${
                            isLoadingDelete ? "bg-red-100" : "bg-red-600"
                          } p-2 text-sm text-white rounded-md`}
                          onClick={() => handleDeleteClick(data)}
                          disabled={isLoadingDelete}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center text-secondary p-3">
                    User not found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
      {showEmploymentDetails && (
        <StateGovModal
          selectedInfo={employmentDetails}
          setStateGovModalOpen={setShowEmploymentDetails}
        />
      )}
      <section className="p-3 my-5">
        <Pagination
          length={
            filter.length > 0
              ? filteredUsers.length
              : totalResults
          }
          itemsPerPage={itemsPerPage}
          handlePagination={goToPage}
          currentPage={currentPage}
          prevPage={prevPage}
          nextPage={nextPage}
        />
      </section>
      {viewAddress.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg w-1/3">
            <h2 className="text-lg font-bold mb-4">User Address</h2>
            <p className="text-wrap">
              {viewAddress.address || "No address available"}
            </p>
            <button
              className="mt-4 bg-secondary text-white px-4 py-2 rounded"
              onClick={() => setViewAddress({ show: false, address: "" })}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateUsers;
